import {
    initialScientificAnalysisFilterPanelPatientAgeState,
    ScientificAnalysisFilterPanelPatientAgeState
} from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel-patientage/scientific-analysis-filter-panel-patientage.models'
import {
    ScientificAnalysisConditionComponent,
    ScientificAnalysisStaticImplements
} from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel/scientific-analysis-filter-panel-conditionfactory'
import { ScientificAnalysisSelectedFilter } from '@/modules/scientific-analysis-module/scientific-analysis-module.models'
import {
    ScientificAnalysisComparatorType,
    ScientificAnalysisFieldAdditionalInfo,
    SimpleListEntity,
    SimpleListEntityResponse
} from '@/modules/scientific-analysis-module/services/scientific-analysis-dtos'
import { scientificAnalysisService } from '@/modules/scientific-analysis-module/services/scientific-analysis-services'
import {
    ScientificAnalysisComparisonType,
    ScientificAnalysisCondition,
    ScientificAnalysisConditionValidationErrorCode
} from '@/modules/scientific-analysis-module/shared/scientific-analysis.models'
import { Nullable } from '@/shared/types/app.types'
import { toSafeInteger } from 'lodash'
import { Component, Emit, Prop, Vue } from 'vue-property-decorator'

@Component({
    components: {}
})
// eslint-disable-next-line no-use-before-define
    @ScientificAnalysisStaticImplements<ScientificAnalysisConditionComponent<ScientificAnalysisFilterPanelPatientage>>()
export default class ScientificAnalysisFilterPanelPatientage extends Vue {
    state: ScientificAnalysisFilterPanelPatientAgeState
    static COMPONENT_NAME = 'scientific-analysis-filter-panel-patientage'
    static PATIENT_AGE_METAINFO = 'patientAge'
    static SEPARATOR = '_'
    static ALLOWED_COMPARATORS: ScientificAnalysisComparatorType[] = [
        ScientificAnalysisComparatorType.IN_RANGE
    ]
    @Prop() condition!: ScientificAnalysisCondition
    @Prop() selectedFilter!: ScientificAnalysisSelectedFilter

    constructor() {
        super()
        this.state = initialScientificAnalysisFilterPanelPatientAgeState()
    }

    mounted():void {
        this.onChange()
    }

    static canAccept(comparisonType: ScientificAnalysisComparisonType): boolean {
        return !!comparisonType.additionalInfo && comparisonType.additionalInfo !== null &&
            comparisonType.additionalInfo === ScientificAnalysisFieldAdditionalInfo.selectUnitTime &&
            ScientificAnalysisFilterPanelPatientage.ALLOWED_COMPARATORS.includes(comparisonType.comparatorType)
    }

    async loadSimpleEntityInfo(filter: Nullable<string>): Promise<SimpleListEntityResponse[]> {
        this.state.entities = await scientificAnalysisService.getEntitySimpleList(SimpleListEntity.PATIENT_AGE, filter)
        return this.state.entities
    }

    onSelectTimeUnit(entity: SimpleListEntityResponse): void {
        this.condition.value = `${entity.id}_${this.selectedStartAge ? this.selectedStartAge : ''}`
        this.onChange()
    }

    get selectedEntity(): Nullable<SimpleListEntityResponse> {
        if (this.condition.value) {
            const timeUnitCode = (this.condition.value as string).split(ScientificAnalysisFilterPanelPatientage.SEPARATOR)[0]
            if (timeUnitCode.length > 0) {
                const entityFound = this.state.entities.find(entity => entity.id === timeUnitCode)
                return entityFound ?? this.state.entities[0]
            }
        }
        return null
    }

    get selectedStartAge(): Nullable<number> {
        if (this.condition.value) {
            const startAgeStr = (this.condition.value as string).split(ScientificAnalysisFilterPanelPatientage.SEPARATOR)[1]
            if (startAgeStr.length > 0) {
                return toSafeInteger(startAgeStr)
            }
        }
        return null
    }

    get selectedEndAge(): number {
        return toSafeInteger(this.condition.valueTo)
    }

    get _validateStart(): ScientificAnalysisConditionValidationErrorCode {
        if (this.condition.value.length === 0) {
            return ScientificAnalysisConditionValidationErrorCode.EMPTY
        } else {
            const startAgeStr = (this.condition.value as string).split(ScientificAnalysisFilterPanelPatientage.SEPARATOR)[1]
            if (startAgeStr.length === 0) {
                return ScientificAnalysisConditionValidationErrorCode.EMPTY
            } else if (this.checkDateFromGreatherThanDateTo()) {
                return ScientificAnalysisConditionValidationErrorCode.FROM_GREATERTHAN_TO
            } else {
                return ScientificAnalysisConditionValidationErrorCode.NONE
            }
        }
    }

    get _validateEnd(): ScientificAnalysisConditionValidationErrorCode {
        if (!this.condition.valueTo || this.condition.valueTo.length === 0) {
            return ScientificAnalysisConditionValidationErrorCode.EMPTY
        } else {
            if (this.checkDateFromGreatherThanDateTo()) {
                return ScientificAnalysisConditionValidationErrorCode.FROM_GREATERTHAN_TO
            } else {
                return ScientificAnalysisConditionValidationErrorCode.NONE
            }
        }
    }

    get _validateEntity(): ScientificAnalysisConditionValidationErrorCode {
        if (this.condition.value.length === 0) {
            return ScientificAnalysisConditionValidationErrorCode.EMPTY
        } else {
            const timeUnitCode = (this.condition.value as string).split(ScientificAnalysisFilterPanelPatientage.SEPARATOR)[0]
            if (timeUnitCode.length === 0) {
                return ScientificAnalysisConditionValidationErrorCode.EMPTY
            } else {
                return ScientificAnalysisConditionValidationErrorCode.NONE
            }
        }
    }

    private checkDateFromGreatherThanDateTo(): boolean {
        const startAgeStr = (this.condition.value as string).split(ScientificAnalysisFilterPanelPatientage.SEPARATOR)[1]
        if (startAgeStr && this.condition.valueTo && parseInt(startAgeStr) > parseInt(this.condition.valueTo as string)) {
            return true
        }
        return false
    }

    private validateGeneral():void {
        let errorCode = this._validateEntity
        if (errorCode === ScientificAnalysisConditionValidationErrorCode.NONE) {
            errorCode = this._validateStart
            if (errorCode === ScientificAnalysisConditionValidationErrorCode.NONE) {
                errorCode = this._validateEnd
            }
        }
        this.condition.validationErrorCode = errorCode
    }

    _onChangeStart(start: number):void {
        this.condition.value = `${this.selectedEntity ? this.selectedEntity.id : ''}${ScientificAnalysisFilterPanelPatientage.SEPARATOR}${start}`
        this.onChange()
    }

    @Emit('modification')
    private onChange(): void {
        this.validateGeneral()
        this.condition.metaInfo = [ScientificAnalysisFilterPanelPatientage.PATIENT_AGE_METAINFO, this.selectedEntity ? this.selectedEntity.label : '']
    }
}
